import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { toast } from 'sonner';
import api from '@/lib/api';
import {
  ShoppingCart,
  Users,
  Target,
  Euro,
  TrendingUp,
  Plus,
  Search,
  Filter,
  RefreshCw,
  Loader2,
  FileText,
  Megaphone,
  Code,
  BarChart3,
  ArrowRight,
  ArrowLeft,
  CheckCircle2,
  XCircle,
  Clock,
  Send,
  Eye,
  Trash2,
  Edit,
  UserPlus,
  Building2,
  Settings,
  Mail,
  Globe,
  Lock,
  Copy,
  ExternalLink,
  Sparkles,
  Package,
  Tractor,
  Calculator,
  Map,
  Droplets,
  Calendar,
  Wallet,
  Check,
  Cloud,
  Heart,
  Rabbit,
  PlusCircle,
  MinusCircle,
  ClipboardCheck
} from 'lucide-react';
import AuditVenteTab from '@/components/AuditVenteTab';

// ID de la société autorisée
const SOCIETE_FRED_DEV = "d3db4e3f-c6ac-471d-97a8-e58fd382f0cc";

// Tarification par nombre de sociétés
const TARIFS_SOCIETES = [
  { nb: 1, label: '1 société', prix_base: 0 },
  { nb: 2, label: '2 sociétés', prix_base: 15 },
  { nb: 3, label: '3 sociétés', prix_base: 25 },
  { nb: 5, label: '4-5 sociétés', prix_base: 40 },
  { nb: 10, label: '6-10 sociétés', prix_base: 60 },
  { nb: 99, label: 'Illimité', prix_base: 100 }
];

// Fiches métiers / Options supplémentaires
const FICHES_METIERS = [
  { id: 'viticulture', nom: 'Viticulture', description: 'Registre cave, vinification, lots', prix: 20 },
  { id: 'ppam', nom: 'PPAM / Aromatiques', description: 'Distillation, séchage, huiles essentielles', prix: 25 },
  { id: 'arboriculture', nom: 'Arboriculture', description: 'Vergers, récolte, calibrage', prix: 15 },
  { id: 'maraichage', nom: 'Maraîchage', description: 'Planches, rotations, paniers', prix: 15 },
  { id: 'elevage', nom: 'Élevage', description: 'Troupeaux, sanitaire, reproduction', prix: 25 },
  { id: 'grandes_cultures', nom: 'Grandes cultures', description: 'Assolement, PAC, rendements', prix: 15 },
  { id: 'bio', nom: 'Certification Bio', description: 'Traçabilité AB, contrôles, documents', prix: 20 },
  { id: 'eta', nom: 'ETA / Prestation', description: 'Chantiers, facturation, planning', prix: 30 }
];

// Modules disponibles pour les clients
const MODULES_DISPONIBLES = [
  { id: 'dashboard', nom: 'Tableau de bord', icon: BarChart3, description: 'Vue d\'ensemble de l\'exploitation', inclus: true },
  { id: 'parcellaire', nom: 'Parcellaire', icon: Map, description: 'Gestion des parcelles et carte interactive', prix: 15 },
  { id: 'interventions', nom: 'Interventions', icon: Calendar, description: 'Planification et suivi des travaux', prix: 20 },
  { id: 'materiel', nom: 'Matériel', icon: Tractor, description: 'Gestion du parc matériel', prix: 15 },
  { id: 'stocks', nom: 'Stocks', icon: Package, description: 'Inventaire et mouvements', prix: 10 },
  { id: 'meteo', nom: 'Météo & Alertes', icon: Cloud, description: 'Prévisions et alertes paramétrables', prix: 10 },
  { id: 'comptabilite', nom: 'Comptabilité', icon: Calculator, description: 'Suivi financier simplifié', prix: 25 },
  { id: 'registre_phyto', nom: 'Registre Phyto', icon: Droplets, description: 'Traçabilité phytosanitaire + EPHY', prix: 30 },
  { id: 'crm', nom: 'CRM', icon: Users, description: 'Gestion clients et ventes', prix: 20 },
  { id: 'finance', nom: 'Finance', icon: Wallet, description: 'Tableaux de bord financiers', prix: 15 },
  { id: 'agents_ia', nom: 'Agents IA', icon: Sparkles, description: 'Assistant CASIA + Distillateur', prix: 50, premium: true },
  { id: 'elevage', nom: 'Élevage', icon: Heart, description: 'Gestion du troupeau et santé', prix: 25 },
  { id: 'equitation', nom: 'Équitation', icon: Rabbit, description: 'Pensions, boxes, facturation', prix: 30 },
  { id: 'facturation', nom: 'Facturation', icon: FileText, description: 'Devis, factures, BL', prix: 20 }
];

// Types d'activités pour les sociétés
const TYPES_ACTIVITE = [
  { id: 'exploitation_agricole', nom: 'Exploitation Agricole', description: 'Cultures, élevage, parcellaire' },
  { id: 'commerce', nom: 'Commerce / Vente', description: 'Activité commerciale, CRM' },
  { id: 'elevage_lapin', nom: 'Élevage Lapins', description: 'Cuniculture avec génétique' },
  { id: 'equitation', nom: 'Centre Équestre', description: 'Pensions, cours, paddocks' },
  { id: 'transformation', nom: 'Transformation', description: 'Distillerie, fromagerie...' },
  { id: 'projet', nom: 'Projet en cours', description: 'Espace de développement' }
];

// Agents IA disponibles
const AGENTS_IA_DISPONIBLES = [
  { id: 'eleveur_lapin', nom: 'Agent Éleveur Lapins', description: 'Santé, reproduction, génétique', icon: '🐰' },
  { id: 'veterinaire', nom: 'Agent Vétérinaire', description: 'Alertes santé, conseils (avec disclaimer)', icon: '🩺' },
  { id: 'commercial', nom: 'Agent Commercial', description: 'Téléprospection, suivi leads', icon: '📞' },
  { id: 'equitation', nom: 'Agent Équitation', description: 'Gestion pensions, planning cours', icon: '🐴' },
  { id: 'marketing', nom: 'Agent Marketing', description: 'Publicité, réseaux sociaux', icon: '📣' },
  { id: 'comptable', nom: 'Agent Comptable', description: 'Suivi financier, alertes', icon: '💰' }
];

const VenteBureauAgricolePage = () => {
  const { activeSociete } = useWorkspace();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');
  
  // Data
  const [dashboard, setDashboard] = useState(null);
  const [tarifs, setTarifs] = useState([]);
  const [prospects, setProspects] = useState([]);
  const [campagnes, setCampagnes] = useState([]);
  const [devis, setDevis] = useState([]);
  const [clients, setClients] = useState([]);
  
  // Wizard création client
  const [showWizard, setShowWizard] = useState(false);
  const [wizardStep, setWizardStep] = useState(1);
  const [newClient, setNewClient] = useState({
    // Contact principal
    contact_nom: '',
    contact_prenom: '',
    contact_email: '',
    contact_telephone: '',
    // Sociétés multiples
    societes: [],
    // Fallback single société (rétrocompatibilité)
    raison_sociale: '',
    forme_juridique: 'EARL',
    siret: '',
    adresse: '',
    code_postal: '',
    ville: '',
    // Modules
    modules_selectionnes: ['dashboard'],
    fiches_metiers: [],
    nb_societes: 1,
    nb_utilisateurs: 1,
    // Personnalisation
    nom_application: 'Mon Exploitation',
    couleur_principale: '#16a34a',
    couleur_secondaire: '#166534',
    logo_url: '',
    // Tarification
    tarif_mensuel: 0,
    engagement_mois: 12,
    remise_pourcent: 0
  });
  const [createdCredentials, setCreatedCredentials] = useState(null);
  
  // Dialogs
  const [showNewTarif, setShowNewTarif] = useState(false);
  const [showNewProspect, setShowNewProspect] = useState(false);
  const [showNewCampagne, setShowNewCampagne] = useState(false);
  const [showClientDetails, setShowClientDetails] = useState(null);
  
  // Forms
  const [newTarif, setNewTarif] = useState({
    nom_produit: '',
    type_licence: 'annuel',
    prix_ht: '',
    nb_utilisateurs_inclus: 1,
    modules_inclus: [],
    actif: true
  });
  
  const [newProspect, setNewProspect] = useState({
    raison_sociale: '',
    contact_nom: '',
    contact_email: '',
    contact_telephone: '',
    ville: '',
    source: 'web',
    statut: 'nouveau',
    ca_estime: ''
  });
  
  const [newCampagne, setNewCampagne] = useState({
    nom: '',
    canal: 'google_ads',
    budget: '',
    date_debut: new Date().toISOString().slice(0, 10),
    objectif: '',
    statut: 'planifie'
  });

  // Check access - uniquement Frédéric Développement
  const hasAccess = activeSociete?.id === SOCIETE_FRED_DEV || 
                    activeSociete?.nom === "Frédéric Développement";

  const loadData = useCallback(async () => {
    if (!hasAccess) {
      setLoading(false);
      return;
    }
    
    setLoading(true);
    try {
      const [dashRes, tarifsRes, prospectsRes, campagnesRes, devisRes, clientsRes] = await Promise.all([
        api.get('/vente-bureau-agricole/dashboard').catch(() => ({ data: null })),
        api.get('/vente-bureau-agricole/tarifs').catch(() => ({ data: { tarifs: [] } })),
        api.get('/vente-bureau-agricole/prospects').catch(() => ({ data: { prospects: [] } })),
        api.get('/vente-bureau-agricole/campagnes').catch(() => ({ data: { campagnes: [] } })),
        api.get('/vente-bureau-agricole/devis').catch(() => ({ data: { devis: [] } })),
        api.get('/vente-bureau-agricole/clients').catch(() => ({ data: { clients: [] } }))
      ]);
      
      setDashboard(dashRes.data);
      setTarifs(tarifsRes.data?.tarifs || []);
      setProspects(prospectsRes.data?.prospects || []);
      setCampagnes(campagnesRes.data?.campagnes || []);
      setDevis(devisRes.data?.devis || []);
      setClients(clientsRes.data?.clients || []);
    } catch (error) {
      console.error('Erreur chargement:', error);
    } finally {
      setLoading(false);
    }
  }, [hasAccess]);

  useEffect(() => {
    loadData();
  }, [loadData, activeSociete]);

  // Calcul du tarif mensuel basé sur les modules sélectionnés
  useEffect(() => {
    const basePrix = 29; // Prix de base mensuel
    const prixModules = newClient.modules_selectionnes.reduce((total, modId) => {
      const module = MODULES_DISPONIBLES.find(m => m.id === modId);
      return total + (module?.prix || 0);
    }, 0);
    const prixUtilisateurs = (newClient.nb_utilisateurs - 1) * 10;
    const sousTotal = basePrix + prixModules + prixUtilisateurs;
    const remise = sousTotal * (newClient.remise_pourcent / 100);
    setNewClient(c => ({ ...c, tarif_mensuel: Math.round(sousTotal - remise) }));
  }, [newClient.modules_selectionnes, newClient.nb_utilisateurs, newClient.remise_pourcent]);

  const createClient = async () => {
    try {
      const response = await api.post('/vente-bureau-agricole/clients', newClient);
      setCreatedCredentials(response.data.credentials);
      toast.success('Client créé avec succès !');
      setWizardStep(6); // Étape récapitulative
      loadData();
    } catch (error) {
      toast.error('Erreur lors de la création du client');
    }
  };

  const sendCredentialsEmail = async () => {
    try {
      await api.post('/vente-bureau-agricole/clients/send-credentials', {
        client_id: createdCredentials.client_id,
        email: newClient.contact_email
      });
      toast.success('Email envoyé au client !');
    } catch (error) {
      toast.error('Erreur lors de l\'envoi');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copié !');
  };

  const resetWizard = () => {
    setShowWizard(false);
    setWizardStep(1);
    setNewClient({
      // Contact principal
      contact_nom: '', contact_prenom: '', contact_email: '', contact_telephone: '',
      // Sociétés multiples
      societes: [],
      // Fallback single société (rétrocompatibilité)
      raison_sociale: '', forme_juridique: 'EARL', siret: '', adresse: '',
      code_postal: '', ville: '',
      // Modules
      modules_selectionnes: ['dashboard'],
      nb_utilisateurs: 1, 
      // Personnalisation
      nom_application: 'Mon Exploitation', 
      couleur_principale: '#16a34a',
      couleur_secondaire: '#166534',
      logo_url: '', 
      // Tarification
      tarif_mensuel: 0, engagement_mois: 12, remise_pourcent: 0
    });
    setCreatedCredentials(null);
  };

  // Pré-remplir le formulaire pour la démo Elsa Rigolier
  const prefillElsaDemo = () => {
    setNewClient({
      contact_nom: 'Rigolier',
      contact_prenom: 'Elsa',
      contact_email: 'rigollierelsa@gmail.com',
      contact_telephone: '',
      societes: [
        {
          nom: 'Exploitation Agricole Rigolier',
          forme_juridique: 'EARL',
          siret: '',
          adresse: '',
          code_postal: '',
          ville: 'Lyon',
          type_activite: 'exploitation_agricole',
          modules_specifiques: ['parcellaire', 'elevage', 'crm'],
          description: 'Parcellaire, élevage de lapins, vente de foin',
          agents_ia: ['eleveur_lapin', 'veterinaire']
        },
        {
          nom: 'Elsa Rome',
          forme_juridique: 'SAS',
          siret: '',
          adresse: '',
          code_postal: '',
          ville: 'Lyon',
          type_activite: 'commerce',
          modules_specifiques: ['crm', 'facturation'],
          description: 'Société commerciale avec téléprospection',
          agents_ia: ['commercial']
        },
        {
          nom: 'Projet Centre Équestre',
          forme_juridique: 'Projet',
          siret: '',
          adresse: '',
          code_postal: '',
          ville: 'Lyon',
          type_activite: 'equitation',
          modules_specifiques: ['equitation', 'facturation', 'crm'],
          description: 'Pensions chevaux, cours, paddocks, site web',
          agents_ia: ['equitation']
        }
      ],
      raison_sociale: '',
      forme_juridique: 'EARL',
      siret: '',
      adresse: '',
      code_postal: '',
      ville: '',
      modules_selectionnes: ['dashboard', 'parcellaire', 'elevage', 'crm', 'equitation', 'facturation', 'agents_ia'],
      nb_utilisateurs: 3,
      nom_application: 'Espace Elsa Rigolier',
      couleur_principale: '#8b5cf6',
      couleur_secondaire: '#6d28d9',
      logo_url: '',
      tarif_mensuel: 0,
      engagement_mois: 1, // 1 mois gratuit de démo
      remise_pourcent: 100 // 100% de remise pour la démo
    });
    setShowWizard(true);
    toast.success('Formulaire pré-rempli pour Elsa Rigolier !');
  };

  // Ajouter une société au client
  const addSociete = () => {
    setNewClient(c => ({
      ...c,
      societes: [...c.societes, {
        nom: '',
        forme_juridique: 'EARL',
        siret: '',
        adresse: '',
        code_postal: '',
        ville: '',
        type_activite: 'exploitation_agricole',
        modules_specifiques: [],
        agents_ia: [],
        description: ''
      }]
    }));
  };

  // Supprimer une société
  const removeSociete = (index) => {
    setNewClient(c => ({
      ...c,
      societes: c.societes.filter((_, i) => i !== index)
    }));
  };

  // Mettre à jour une société
  const updateSociete = (index, field, value) => {
    setNewClient(c => ({
      ...c,
      societes: c.societes.map((soc, i) => 
        i === index ? { ...soc, [field]: value } : soc
      )
    }));
  };

  // Toggle module spécifique pour une société
  const toggleSocieteModule = (socIndex, moduleId) => {
    setNewClient(c => ({
      ...c,
      societes: c.societes.map((soc, i) => {
        if (i !== socIndex) return soc;
        const modules = soc.modules_specifiques || [];
        return {
          ...soc,
          modules_specifiques: modules.includes(moduleId)
            ? modules.filter(m => m !== moduleId)
            : [...modules, moduleId]
        };
      })
    }));
  };

  // Toggle agent IA pour une société
  const toggleSocieteAgent = (socIndex, agentId) => {
    setNewClient(c => ({
      ...c,
      societes: c.societes.map((soc, i) => {
        if (i !== socIndex) return soc;
        const agents = soc.agents_ia || [];
        return {
          ...soc,
          agents_ia: agents.includes(agentId)
            ? agents.filter(a => a !== agentId)
            : [...agents, agentId]
        };
      })
    }));
  };

  const toggleModule = (moduleId) => {
    const module = MODULES_DISPONIBLES.find(m => m.id === moduleId);
    if (module?.inclus) return; // Module de base, ne peut pas être désélectionné
    
    setNewClient(c => ({
      ...c,
      modules_selectionnes: c.modules_selectionnes.includes(moduleId)
        ? c.modules_selectionnes.filter(m => m !== moduleId)
        : [...c.modules_selectionnes, moduleId]
    }));
  };

  const createTarif = async () => {
    try {
      await api.post('/vente-bureau-agricole/tarifs', {
        ...newTarif,
        prix_ht: parseFloat(newTarif.prix_ht) || 0
      });
      toast.success('Tarif créé');
      setShowNewTarif(false);
      setNewTarif({ nom_produit: '', type_licence: 'annuel', prix_ht: '', nb_utilisateurs_inclus: 1, modules_inclus: [], actif: true });
      loadData();
    } catch (error) {
      toast.error('Erreur lors de la création');
    }
  };

  const createProspect = async () => {
    try {
      await api.post('/vente-bureau-agricole/prospects', {
        ...newProspect,
        ca_estime: newProspect.ca_estime ? parseFloat(newProspect.ca_estime) : null
      });
      toast.success('Prospect ajouté au pipeline');
      setShowNewProspect(false);
      setNewProspect({ raison_sociale: '', contact_nom: '', contact_email: '', contact_telephone: '', ville: '', source: 'web', statut: 'nouveau', ca_estime: '' });
      loadData();
    } catch (error) {
      toast.error('Erreur lors de l\'ajout');
    }
  };

  const createCampagne = async () => {
    try {
      await api.post('/vente-bureau-agricole/campagnes', {
        ...newCampagne,
        budget: parseFloat(newCampagne.budget) || 0
      });
      toast.success('Campagne créée');
      setShowNewCampagne(false);
      setNewCampagne({ nom: '', canal: 'google_ads', budget: '', date_debut: new Date().toISOString().slice(0, 10), objectif: '', statut: 'planifie' });
      loadData();
    } catch (error) {
      toast.error('Erreur lors de la création');
    }
  };

  const getStatutBadge = (statut) => {
    const colors = {
      nouveau: 'bg-blue-100 text-blue-800',
      contacte: 'bg-yellow-100 text-yellow-800',
      demo_planifiee: 'bg-purple-100 text-purple-800',
      negociation: 'bg-orange-100 text-orange-800',
      gagne: 'bg-green-100 text-green-800',
      perdu: 'bg-red-100 text-red-800',
      actif: 'bg-green-100 text-green-800',
      suspendu: 'bg-red-100 text-red-800'
    };
    const labels = {
      nouveau: 'Nouveau', contacte: 'Contacté', demo_planifiee: 'Démo planifiée',
      negociation: 'Négociation', gagne: 'Gagné', perdu: 'Perdu',
      actif: 'Actif', suspendu: 'Suspendu'
    };
    return <Badge className={colors[statut] || 'bg-gray-100'}>{labels[statut] || statut}</Badge>;
  };

  // Access denied
  if (!hasAccess) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh] text-center p-6">
        <div className="p-6 rounded-full bg-red-100 mb-6">
          <XCircle className="w-16 h-16 text-red-600" />
        </div>
        <h1 className="text-2xl font-bold mb-2">Accès Restreint</h1>
        <p className="text-muted-foreground max-w-md mb-4">
          Ce module est réservé à <strong>Frédéric Développement</strong> pour la distribution du logiciel Bureau Agricole en marque blanche.
        </p>
        <p className="text-sm text-muted-foreground">
          Société actuelle: <strong>{activeSociete?.nom || 'Non sélectionnée'}</strong>
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="vente-bureau-agricole-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <ShoppingCart className="w-7 h-7 text-green-600" />
            Distribution Bureau Agricole
          </h1>
          <p className="text-muted-foreground">
            Créez et gérez vos clients en marque blanche
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={loadData} variant="ghost" size="icon">
            <RefreshCw className="w-4 h-4" />
          </Button>
          <Button onClick={() => setShowWizard(true)} className="bg-green-600 hover:bg-green-700">
            <UserPlus className="w-4 h-4 mr-2" />
            Créer un client
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="dashboard" className="gap-2">
            <BarChart3 className="w-4 h-4" />
            Dashboard
          </TabsTrigger>
          <TabsTrigger value="clients" className="gap-2">
            <Building2 className="w-4 h-4" />
            Clients ({clients.length})
          </TabsTrigger>
          <TabsTrigger value="prospects" className="gap-2">
            <Users className="w-4 h-4" />
            Pipeline ({prospects.length})
          </TabsTrigger>
          <TabsTrigger value="tarifs" className="gap-2">
            <Euro className="w-4 h-4" />
            Tarifs
          </TabsTrigger>
          <TabsTrigger value="campagnes" className="gap-2">
            <Megaphone className="w-4 h-4" />
            Publicité
          </TabsTrigger>
          <TabsTrigger value="audit" className="gap-2" data-testid="tab-audit-vente">
            <ClipboardCheck className="w-4 h-4" />
            Audit & Vente
          </TabsTrigger>
        </TabsList>

        {/* Dashboard */}
        <TabsContent value="dashboard" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-3 rounded-full bg-green-100">
                    <Building2 className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{clients.length}</p>
                    <p className="text-sm text-muted-foreground">Clients actifs</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-3 rounded-full bg-blue-100">
                    <Users className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{prospects.length}</p>
                    <p className="text-sm text-muted-foreground">Prospects</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-3 rounded-full bg-amber-100">
                    <Euro className="w-6 h-6 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{clients.reduce((sum, c) => sum + (c.tarif_mensuel || 0), 0)}€</p>
                    <p className="text-sm text-muted-foreground">MRR (mensuel)</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-3 rounded-full bg-purple-100">
                    <TrendingUp className="w-6 h-6 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{clients.reduce((sum, c) => sum + (c.tarif_mensuel || 0), 0) * 12}€</p>
                    <p className="text-sm text-muted-foreground">ARR (annuel)</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Quick Actions */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card className="cursor-pointer hover:border-green-300 transition-colors" onClick={() => setShowWizard(true)}>
              <CardContent className="pt-6">
                <div className="text-center">
                  <div className="p-4 rounded-full bg-green-100 w-fit mx-auto mb-3">
                    <UserPlus className="w-8 h-8 text-green-600" />
                  </div>
                  <h3 className="font-semibold">Créer un nouveau client</h3>
                  <p className="text-sm text-muted-foreground">Assistant de configuration complet</p>
                </div>
              </CardContent>
            </Card>
            
            <Card 
              className="cursor-pointer hover:border-purple-300 transition-colors bg-gradient-to-br from-purple-50 to-violet-50 border-purple-200" 
              onClick={prefillElsaDemo}
              data-testid="btn-demo-elsa"
            >
              <CardContent className="pt-6">
                <div className="text-center">
                  <div className="p-4 rounded-full bg-purple-100 w-fit mx-auto mb-3">
                    <Sparkles className="w-8 h-8 text-purple-600" />
                  </div>
                  <h3 className="font-semibold text-purple-800">Démo Elsa Rigolier</h3>
                  <p className="text-sm text-purple-600">3 sociétés + Agents IA</p>
                </div>
              </CardContent>
            </Card>
            
            <Card className="cursor-pointer hover:border-blue-300 transition-colors" onClick={() => setShowNewProspect(true)}>
              <CardContent className="pt-6">
                <div className="text-center">
                  <div className="p-4 rounded-full bg-blue-100 w-fit mx-auto mb-3">
                    <Target className="w-8 h-8 text-blue-600" />
                  </div>
                  <h3 className="font-semibold">Ajouter un prospect</h3>
                  <p className="text-sm text-muted-foreground">Nouveau lead dans le pipeline</p>
                </div>
              </CardContent>
            </Card>
            
            <Card className="cursor-pointer hover:border-purple-300 transition-colors" onClick={() => setShowNewCampagne(true)}>
              <CardContent className="pt-6">
                <div className="text-center">
                  <div className="p-4 rounded-full bg-purple-100 w-fit mx-auto mb-3">
                    <Megaphone className="w-8 h-8 text-purple-600" />
                  </div>
                  <h3 className="font-semibold">Lancer une campagne</h3>
                  <p className="text-sm text-muted-foreground">Publicité et acquisition</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Clients */}
        <TabsContent value="clients" className="space-y-4">
          <ScrollArea className="h-[600px]">
            <div className="space-y-3">
              {clients.map(client => (
                <Card key={client.id} className="hover:border-green-200 transition-colors">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-semibold text-lg">{client.raison_sociale}</h3>
                          {getStatutBadge(client.statut || 'actif')}
                          <Badge variant="outline">{client.forme_juridique}</Badge>
                        </div>
                        <div className="grid grid-cols-4 gap-4 text-sm">
                          <div>
                            <p className="text-muted-foreground">Contact</p>
                            <p>{client.contact_prenom} {client.contact_nom}</p>
                            <p className="text-xs text-muted-foreground">{client.contact_email}</p>
                          </div>
                          <div>
                            <p className="text-muted-foreground">Localisation</p>
                            <p>{client.ville}</p>
                          </div>
                          <div>
                            <p className="text-muted-foreground">Modules</p>
                            <p>{client.modules_selectionnes?.length || 0} actifs</p>
                          </div>
                          <div>
                            <p className="text-muted-foreground">Tarif mensuel</p>
                            <p className="font-semibold text-green-600">{client.tarif_mensuel}€/mois</p>
                          </div>
                        </div>
                        {client.nom_application && client.nom_application !== 'Bureau Agricole' && (
                          <div className="mt-2 flex items-center gap-2">
                            <Globe className="w-3 h-3 text-muted-foreground" />
                            <span className="text-xs text-muted-foreground">Marque blanche: {client.nom_application}</span>
                          </div>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={() => setShowClientDetails(client)}>
                          <Eye className="w-4 h-4 mr-1" />
                          Détails
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
              
              {clients.length === 0 && (
                <div className="text-center py-12 text-muted-foreground">
                  <Building2 className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>Aucun client pour le moment</p>
                  <Button className="mt-4" onClick={() => setShowWizard(true)}>
                    <UserPlus className="w-4 h-4 mr-2" />
                    Créer votre premier client
                  </Button>
                </div>
              )}
            </div>
          </ScrollArea>
        </TabsContent>

        {/* Prospects */}
        <TabsContent value="prospects" className="space-y-4">
          <div className="flex justify-end">
            <Button onClick={() => setShowNewProspect(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Nouveau prospect
            </Button>
          </div>

          <ScrollArea className="h-[500px]">
            <div className="space-y-3">
              {prospects.map(prospect => (
                <Card key={prospect.id}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-3">
                        <h3 className="font-semibold">{prospect.raison_sociale}</h3>
                        {getStatutBadge(prospect.statut)}
                      </div>
                      {prospect.ca_estime > 0 && (
                        <Badge variant="outline">{prospect.ca_estime.toLocaleString()}€</Badge>
                      )}
                    </div>
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div>
                        <p className="text-muted-foreground">Contact</p>
                        <p>{prospect.contact_nom || '-'}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Email</p>
                        <p>{prospect.contact_email || '-'}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Source</p>
                        <p className="capitalize">{prospect.source}</p>
                      </div>
                    </div>
                    {prospect.statut === 'gagne' && (
                      <div className="mt-3">
                        <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => {
                          setNewClient(c => ({
                            ...c,
                            raison_sociale: prospect.raison_sociale,
                            contact_nom: prospect.contact_nom?.split(' ').slice(-1)[0] || '',
                            contact_prenom: prospect.contact_nom?.split(' ').slice(0, -1).join(' ') || '',
                            contact_email: prospect.contact_email,
                            contact_telephone: prospect.contact_telephone,
                            ville: prospect.ville
                          }));
                          setShowWizard(true);
                        }}>
                          <UserPlus className="w-3 h-3 mr-1" />
                          Convertir en client
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
              
              {prospects.length === 0 && (
                <div className="text-center py-12 text-muted-foreground">
                  <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>Aucun prospect</p>
                </div>
              )}
            </div>
          </ScrollArea>
        </TabsContent>

        {/* Tarifs */}
        <TabsContent value="tarifs" className="space-y-4">
          <div className="flex justify-end">
            <Button onClick={() => setShowNewTarif(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Nouveau tarif
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {tarifs.map(tarif => (
              <Card key={tarif.id} className={!tarif.actif ? 'opacity-50' : ''}>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span>{tarif.nom_produit}</span>
                    {tarif.actif ? (
                      <Badge className="bg-green-100 text-green-800">Actif</Badge>
                    ) : (
                      <Badge variant="secondary">Inactif</Badge>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-center mb-4">
                    <p className="text-3xl font-bold text-primary">{tarif.prix_ht}€</p>
                    <p className="text-sm text-muted-foreground">HT / {tarif.type_licence}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Campagnes */}
        <TabsContent value="campagnes" className="space-y-4">
          <div className="flex justify-end">
            <Button onClick={() => setShowNewCampagne(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Nouvelle campagne
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {campagnes.map(campagne => (
              <Card key={campagne.id}>
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center justify-between text-base">
                    <span>{campagne.nom}</span>
                    <Badge className={
                      campagne.statut === 'actif' ? 'bg-green-100 text-green-800' :
                      campagne.statut === 'planifie' ? 'bg-blue-100 text-blue-800' :
                      'bg-gray-100 text-gray-800'
                    }>
                      {campagne.statut}
                    </Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-muted-foreground">Canal</p>
                      <p className="capitalize">{campagne.canal?.replace('_', ' ')}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Budget</p>
                      <p className="font-semibold">{campagne.budget?.toLocaleString()}€</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Audit & Vente */}
        <TabsContent value="audit" className="space-y-6" data-testid="audit-vente-content">
          <AuditVenteTab />
        </TabsContent>
      </Tabs>

      {/* ============================================= */}
      {/* WIZARD CRÉATION CLIENT MARQUE BLANCHE */}
      {/* ============================================= */}
      <Dialog open={showWizard} onOpenChange={(open) => !open && resetWizard()}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-green-600" />
              Créer un nouveau client
            </DialogTitle>
            <DialogDescription>
              Étape {wizardStep} sur 6 - Configuration complète en marque blanche
            </DialogDescription>
          </DialogHeader>

          {/* Progress bar */}
          <div className="flex gap-1 mb-4">
            {[1, 2, 3, 4, 5, 6].map(step => (
              <div
                key={step}
                className={`h-2 flex-1 rounded ${step <= wizardStep ? 'bg-green-600' : 'bg-muted'}`}
              />
            ))}
          </div>

          {/* Étape 1: Sociétés / Espaces */}
          {wizardStep === 1 && (
            <div className="space-y-4">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-green-600" />
                  <h3 className="font-semibold">Espaces / Sociétés du client</h3>
                </div>
                <Button size="sm" onClick={addSociete} variant="outline">
                  <PlusCircle className="w-4 h-4 mr-2" />
                  Ajouter un espace
                </Button>
              </div>
              
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800 mb-4">
                <strong>Multi-espaces :</strong> Vous pouvez créer plusieurs sociétés/projets pour ce client. 
                Chaque espace aura ses propres modules et agents IA.
              </div>
              
              {newClient.societes.length === 0 ? (
                <div className="text-center py-8 border-2 border-dashed rounded-lg">
                  <Building2 className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
                  <p className="text-muted-foreground mb-3">Aucune société ajoutée</p>
                  <Button onClick={addSociete}>
                    <PlusCircle className="w-4 h-4 mr-2" />
                    Créer le premier espace
                  </Button>
                </div>
              ) : (
                <ScrollArea className="max-h-[400px]">
                  <div className="space-y-4">
                    {newClient.societes.map((soc, idx) => (
                      <Card key={idx} className="border-l-4 border-l-green-500">
                        <CardHeader className="pb-2">
                          <div className="flex items-center justify-between">
                            <CardTitle className="text-base flex items-center gap-2">
                              <Badge variant="outline">Espace {idx + 1}</Badge>
                              {soc.nom || 'Nouvelle société'}
                            </CardTitle>
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              className="text-red-500 hover:text-red-700"
                              onClick={() => removeSociete(idx)}
                            >
                              <MinusCircle className="w-4 h-4" />
                            </Button>
                          </div>
                        </CardHeader>
                        <CardContent className="space-y-3">
                          <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1">
                              <Label className="text-xs">Nom *</Label>
                              <Input
                                value={soc.nom}
                                onChange={(e) => updateSociete(idx, 'nom', e.target.value)}
                                placeholder="Ex: EARL Les Vignes"
                              />
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs">Type d'activité</Label>
                              <Select 
                                value={soc.type_activite} 
                                onValueChange={(v) => updateSociete(idx, 'type_activite', v)}
                              >
                                <SelectTrigger><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  {TYPES_ACTIVITE.map(t => (
                                    <SelectItem key={t.id} value={t.id}>{t.nom}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                          
                          <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1">
                              <Label className="text-xs">Forme juridique</Label>
                              <Select 
                                value={soc.forme_juridique} 
                                onValueChange={(v) => updateSociete(idx, 'forme_juridique', v)}
                              >
                                <SelectTrigger><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="EARL">EARL</SelectItem>
                                  <SelectItem value="GAEC">GAEC</SelectItem>
                                  <SelectItem value="SCEA">SCEA</SelectItem>
                                  <SelectItem value="SAS">SAS</SelectItem>
                                  <SelectItem value="SARL">SARL</SelectItem>
                                  <SelectItem value="EI">EI (individuel)</SelectItem>
                                  <SelectItem value="Projet">Projet</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs">Ville</Label>
                              <Input
                                value={soc.ville}
                                onChange={(e) => updateSociete(idx, 'ville', e.target.value)}
                                placeholder="Ex: Lyon"
                              />
                            </div>
                          </div>
                          
                          <div className="space-y-1">
                            <Label className="text-xs">Description (optionnel)</Label>
                            <Input
                              value={soc.description}
                              onChange={(e) => updateSociete(idx, 'description', e.target.value)}
                              placeholder="Ex: Production de plantes aromatiques"
                            />
                          </div>
                          
                          {/* Agents IA spécifiques */}
                          <div className="space-y-2 pt-2 border-t">
                            <Label className="text-xs">Agents IA pour cet espace</Label>
                            <div className="flex flex-wrap gap-2">
                              {AGENTS_IA_DISPONIBLES.map(agent => (
                                <Badge
                                  key={agent.id}
                                  variant={(soc.agents_ia || []).includes(agent.id) ? "default" : "outline"}
                                  className="cursor-pointer"
                                  onClick={() => toggleSocieteAgent(idx, agent.id)}
                                >
                                  <span className="mr-1">{agent.icon}</span>
                                  {agent.nom}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </ScrollArea>
              )}
            </div>
          )}

          {/* Étape 2: Contact (INCHANGÉE mais mise à jour style) */}
          {wizardStep === 2 && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 mb-4">
                <Users className="w-5 h-5 text-green-600" />
                <h3 className="font-semibold">Contact principal</h3>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Prénom *</Label>
                  <Input
                    value={newClient.contact_prenom}
                    onChange={(e) => setNewClient(c => ({ ...c, contact_prenom: e.target.value }))}
                    placeholder="Jean"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Nom *</Label>
                  <Input
                    value={newClient.contact_nom}
                    onChange={(e) => setNewClient(c => ({ ...c, contact_nom: e.target.value }))}
                    placeholder="Dupont"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Email *</Label>
                <Input
                  type="email"
                  value={newClient.contact_email}
                  onChange={(e) => setNewClient(c => ({ ...c, contact_email: e.target.value }))}
                  placeholder="jean.dupont@exploitation.fr"
                />
                <p className="text-xs text-muted-foreground">
                  Les identifiants de connexion seront envoyés à cette adresse
                </p>
              </div>
              
              <div className="space-y-2">
                <Label>Téléphone</Label>
                <Input
                  value={newClient.contact_telephone}
                  onChange={(e) => setNewClient(c => ({ ...c, contact_telephone: e.target.value }))}
                  placeholder="06 12 34 56 78"
                />
              </div>
            </div>
          )}

          {/* Étape 3: Modules */}
          {wizardStep === 3 && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 mb-4">
                <Package className="w-5 h-5 text-green-600" />
                <h3 className="font-semibold">Sélection des modules</h3>
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                {MODULES_DISPONIBLES.map(module => {
                  const Icon = module.icon;
                  const isSelected = newClient.modules_selectionnes.includes(module.id);
                  
                  return (
                    <div
                      key={module.id}
                      onClick={() => toggleModule(module.id)}
                      className={`p-3 border rounded-lg cursor-pointer transition-all ${
                        isSelected ? 'border-green-500 bg-green-50' : 'border-border hover:border-green-200'
                      } ${module.inclus ? 'opacity-75' : ''}`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`p-2 rounded ${isSelected ? 'bg-green-100' : 'bg-muted'}`}>
                          <Icon className={`w-4 h-4 ${isSelected ? 'text-green-600' : 'text-muted-foreground'}`} />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <span className="font-medium text-sm">{module.nom}</span>
                            {module.inclus ? (
                              <Badge variant="secondary" className="text-xs">Inclus</Badge>
                            ) : module.premium ? (
                              <Badge className="bg-purple-100 text-purple-800 text-xs">+{module.prix}€</Badge>
                            ) : (
                              <Badge variant="outline" className="text-xs">+{module.prix}€</Badge>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground mt-1">{module.description}</p>
                        </div>
                        {isSelected && <Check className="w-4 h-4 text-green-600" />}
                      </div>
                    </div>
                  );
                })}
              </div>
              
              <div className="space-y-2 pt-4 border-t">
                <Label>Nombre d'utilisateurs</Label>
                <div className="flex items-center gap-4">
                  <Input
                    type="number"
                    min="1"
                    max="50"
                    value={newClient.nb_utilisateurs}
                    onChange={(e) => setNewClient(c => ({ ...c, nb_utilisateurs: parseInt(e.target.value) || 1 }))}
                    className="w-24"
                  />
                  <span className="text-sm text-muted-foreground">
                    +10€/mois par utilisateur supplémentaire
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Étape 4: Personnalisation marque blanche */}
          {wizardStep === 4 && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 mb-4">
                <Globe className="w-5 h-5 text-green-600" />
                <h3 className="font-semibold">Personnalisation marque blanche</h3>
              </div>
              
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-amber-800">
                  <strong>Marque blanche</strong> : Votre client aura accès à une version personnalisée du logiciel, 
                  sans aucune référence externe. Il ne verra que son propre environnement.
                </p>
              </div>
              
              <div className="space-y-2">
                <Label>Nom de l'application</Label>
                <Input
                  value={newClient.nom_application}
                  onChange={(e) => setNewClient(c => ({ ...c, nom_application: e.target.value }))}
                  placeholder="Mon Exploitation"
                />
                <p className="text-xs text-muted-foreground">
                  Ce nom apparaîtra dans le header et le titre du navigateur
                </p>
              </div>
              
              <div className="space-y-2">
                <Label>Couleur principale</Label>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={newClient.couleur_principale}
                    onChange={(e) => setNewClient(c => ({ ...c, couleur_principale: e.target.value }))}
                    className="w-12 h-10 rounded border cursor-pointer"
                  />
                  <Input
                    value={newClient.couleur_principale}
                    onChange={(e) => setNewClient(c => ({ ...c, couleur_principale: e.target.value }))}
                    className="w-32"
                    placeholder="#16a34a"
                  />
                  <div 
                    className="h-10 flex-1 rounded flex items-center justify-center text-white font-medium"
                    style={{ backgroundColor: newClient.couleur_principale }}
                  >
                    Aperçu
                  </div>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>URL du logo (optionnel)</Label>
                <Input
                  value={newClient.logo_url}
                  onChange={(e) => setNewClient(c => ({ ...c, logo_url: e.target.value }))}
                  placeholder="https://..."
                />
                <p className="text-xs text-muted-foreground">
                  Si vide, le nom de l'application sera affiché en texte
                </p>
              </div>
            </div>
          )}

          {/* Étape 5: Tarification */}
          {wizardStep === 5 && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 mb-4">
                <Euro className="w-5 h-5 text-green-600" />
                <h3 className="font-semibold">Tarification</h3>
              </div>
              
              {/* Récapitulatif du calcul */}
              <Card>
                <CardContent className="pt-6">
                  <div className="space-y-3">
                    <div className="flex justify-between text-sm">
                      <span>Prix de base</span>
                      <span>29€/mois</span>
                    </div>
                    {newClient.modules_selectionnes.filter(m => {
                      const mod = MODULES_DISPONIBLES.find(x => x.id === m);
                      return mod && mod.prix > 0;
                    }).map(modId => {
                      const mod = MODULES_DISPONIBLES.find(x => x.id === modId);
                      return (
                        <div key={modId} className="flex justify-between text-sm">
                          <span>+ {mod.nom}</span>
                          <span>{mod.prix}€/mois</span>
                        </div>
                      );
                    })}
                    {newClient.nb_utilisateurs > 1 && (
                      <div className="flex justify-between text-sm">
                        <span>+ {newClient.nb_utilisateurs - 1} utilisateur(s) supp.</span>
                        <span>{(newClient.nb_utilisateurs - 1) * 10}€/mois</span>
                      </div>
                    )}
                    {newClient.remise_pourcent > 0 && (
                      <div className="flex justify-between text-sm text-green-600">
                        <span>Remise ({newClient.remise_pourcent}%)</span>
                        <span>-{Math.round((newClient.tarif_mensuel / (1 - newClient.remise_pourcent/100)) * newClient.remise_pourcent / 100)}€/mois</span>
                      </div>
                    )}
                    <div className="border-t pt-3 flex justify-between font-bold">
                      <span>Total mensuel</span>
                      <span className="text-green-600 text-xl">{newClient.tarif_mensuel}€/mois</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Engagement (mois)</Label>
                  <Select value={String(newClient.engagement_mois)} onValueChange={(v) => setNewClient(c => ({ ...c, engagement_mois: parseInt(v) }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">Sans engagement</SelectItem>
                      <SelectItem value="6">6 mois</SelectItem>
                      <SelectItem value="12">12 mois (-10%)</SelectItem>
                      <SelectItem value="24">24 mois (-15%)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Remise exceptionnelle (%)</Label>
                  <Input
                    type="number"
                    min="0"
                    max="50"
                    value={newClient.remise_pourcent}
                    onChange={(e) => setNewClient(c => ({ ...c, remise_pourcent: parseFloat(e.target.value) || 0 }))}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Étape 6: Récapitulatif et identifiants */}
          {wizardStep === 6 && createdCredentials && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 mb-4 text-green-600">
                <CheckCircle2 className="w-6 h-6" />
                <h3 className="font-semibold text-lg">Client créé avec succès !</h3>
              </div>
              
              <Card className="bg-green-50 border-green-200">
                <CardContent className="pt-6">
                  <h4 className="font-semibold mb-4">Identifiants de connexion</h4>
                  
                  <div className="space-y-3">
                    <div className="flex items-center justify-between bg-white p-3 rounded border">
                      <div>
                        <p className="text-xs text-muted-foreground">URL d'accès (marque blanche)</p>
                        <p className="font-mono text-sm">{createdCredentials.url}</p>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => copyToClipboard(createdCredentials.url)}>
                        <Copy className="w-4 h-4" />
                      </Button>
                    </div>
                    
                    <div className="flex items-center justify-between bg-white p-3 rounded border">
                      <div>
                        <p className="text-xs text-muted-foreground">Email</p>
                        <p className="font-mono text-sm">{createdCredentials.email}</p>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => copyToClipboard(createdCredentials.email)}>
                        <Copy className="w-4 h-4" />
                      </Button>
                    </div>
                    
                    <div className="flex items-center justify-between bg-white p-3 rounded border">
                      <div>
                        <p className="text-xs text-muted-foreground">Mot de passe temporaire</p>
                        <p className="font-mono text-sm">{createdCredentials.password}</p>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => copyToClipboard(createdCredentials.password)}>
                        <Copy className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  
                  <div className="mt-4 pt-4 border-t border-green-200">
                    <Button onClick={sendCredentialsEmail} className="w-full">
                      <Mail className="w-4 h-4 mr-2" />
                      Envoyer les identifiants par email à {newClient.contact_email}
                    </Button>
                  </div>
                </CardContent>
              </Card>
              
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <p className="text-sm text-amber-800">
                  <strong>Important</strong> : Le client accèdera à son propre environnement personnalisé 
                  ({newClient.nom_application}). Il ne verra aucune référence externe à votre marque.
                </p>
              </div>
            </div>
          )}

          {/* Navigation buttons */}
          <DialogFooter className="flex justify-between">
            <div>
              {wizardStep > 1 && wizardStep < 6 && (
                <Button variant="outline" onClick={() => setWizardStep(s => s - 1)}>
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Précédent
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              {wizardStep < 6 && (
                <Button variant="outline" onClick={resetWizard}>
                  Annuler
                </Button>
              )}
              {wizardStep < 5 && (
                <Button 
                  onClick={() => setWizardStep(s => s + 1)}
                  disabled={
                    (wizardStep === 1 && newClient.societes.length === 0) ||
                    (wizardStep === 1 && newClient.societes.some(s => !s.nom || !s.ville)) ||
                    (wizardStep === 2 && (!newClient.contact_email || !newClient.contact_nom))
                  }
                >
                  Suivant
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              )}
              {wizardStep === 5 && (
                <Button onClick={createClient} className="bg-green-600 hover:bg-green-700">
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  Créer le client
                </Button>
              )}
              {wizardStep === 6 && (
                <Button onClick={resetWizard}>
                  Terminer
                </Button>
              )}
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Détails Client */}
      <Dialog open={!!showClientDetails} onOpenChange={() => setShowClientDetails(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{showClientDetails?.raison_sociale}</DialogTitle>
          </DialogHeader>
          {showClientDetails && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Forme juridique</p>
                  <p className="font-medium">{showClientDetails.forme_juridique}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Ville</p>
                  <p className="font-medium">{showClientDetails.ville}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Contact</p>
                  <p className="font-medium">{showClientDetails.contact_prenom} {showClientDetails.contact_nom}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Email</p>
                  <p className="font-medium">{showClientDetails.contact_email}</p>
                </div>
              </div>
              
              {showClientDetails.nom_application && (
                <div className="bg-muted/50 p-3 rounded-lg">
                  <p className="text-xs text-muted-foreground mb-1">Marque blanche</p>
                  <div className="flex items-center gap-2">
                    <div 
                      className="w-4 h-4 rounded" 
                      style={{ backgroundColor: showClientDetails.couleur_principale || '#16a34a' }}
                    />
                    <span className="font-medium">{showClientDetails.nom_application}</span>
                  </div>
                </div>
              )}
              
              <div>
                <p className="text-muted-foreground text-sm mb-2">Modules activés</p>
                <div className="flex flex-wrap gap-1">
                  {showClientDetails.modules_selectionnes?.map(modId => {
                    const mod = MODULES_DISPONIBLES.find(m => m.id === modId);
                    return mod ? (
                      <Badge key={modId} variant="outline">{mod.nom}</Badge>
                    ) : null;
                  })}
                </div>
              </div>
              
              <div className="border-t pt-4 flex justify-between items-center">
                <div>
                  <p className="text-muted-foreground text-sm">Tarif mensuel</p>
                  <p className="text-xl font-bold text-green-600">{showClientDetails.tarif_mensuel}€/mois</p>
                </div>
                <Button variant="outline">
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Accéder au compte
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog Nouveau Prospect */}
      <Dialog open={showNewProspect} onOpenChange={setShowNewProspect}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nouveau prospect</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Raison sociale *</Label>
              <Input
                value={newProspect.raison_sociale}
                onChange={(e) => setNewProspect(p => ({ ...p, raison_sociale: e.target.value }))}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Contact</Label>
                <Input
                  value={newProspect.contact_nom}
                  onChange={(e) => setNewProspect(p => ({ ...p, contact_nom: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  type="email"
                  value={newProspect.contact_email}
                  onChange={(e) => setNewProspect(p => ({ ...p, contact_email: e.target.value }))}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Source</Label>
                <Select value={newProspect.source} onValueChange={(v) => setNewProspect(p => ({ ...p, source: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="web">Site web</SelectItem>
                    <SelectItem value="salon">Salon</SelectItem>
                    <SelectItem value="recommandation">Recommandation</SelectItem>
                    <SelectItem value="pub">Publicité</SelectItem>
                    <SelectItem value="autre">Autre</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>CA estimé (€)</Label>
                <Input
                  type="number"
                  value={newProspect.ca_estime}
                  onChange={(e) => setNewProspect(p => ({ ...p, ca_estime: e.target.value }))}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewProspect(false)}>Annuler</Button>
            <Button onClick={createProspect} disabled={!newProspect.raison_sociale}>Ajouter</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Nouveau Tarif */}
      <Dialog open={showNewTarif} onOpenChange={setShowNewTarif}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nouveau tarif</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Nom du produit *</Label>
              <Input
                value={newTarif.nom_produit}
                onChange={(e) => setNewTarif(t => ({ ...t, nom_produit: e.target.value }))}
                placeholder="Bureau Agricole Pro"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Prix HT (€) *</Label>
                <Input
                  type="number"
                  value={newTarif.prix_ht}
                  onChange={(e) => setNewTarif(t => ({ ...t, prix_ht: e.target.value }))}
                  placeholder="99"
                />
              </div>
              <div className="space-y-2">
                <Label>Type licence</Label>
                <Select value={newTarif.type_licence} onValueChange={(v) => setNewTarif(t => ({ ...t, type_licence: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="mensuel">Mensuel</SelectItem>
                    <SelectItem value="annuel">Annuel</SelectItem>
                    <SelectItem value="perpetuel">Perpétuel</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewTarif(false)}>Annuler</Button>
            <Button onClick={createTarif} disabled={!newTarif.nom_produit || !newTarif.prix_ht}>Créer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Nouvelle Campagne */}
      <Dialog open={showNewCampagne} onOpenChange={setShowNewCampagne}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nouvelle campagne publicitaire</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Nom de la campagne *</Label>
              <Input
                value={newCampagne.nom}
                onChange={(e) => setNewCampagne(c => ({ ...c, nom: e.target.value }))}
                placeholder="Lancement Bureau Agricole 2026"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Canal</Label>
                <Select value={newCampagne.canal} onValueChange={(v) => setNewCampagne(c => ({ ...c, canal: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="google_ads">Google Ads</SelectItem>
                    <SelectItem value="facebook">Facebook</SelectItem>
                    <SelectItem value="linkedin">LinkedIn</SelectItem>
                    <SelectItem value="email">Emailing</SelectItem>
                    <SelectItem value="salon">Salon</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Budget (€)</Label>
                <Input
                  type="number"
                  value={newCampagne.budget}
                  onChange={(e) => setNewCampagne(c => ({ ...c, budget: e.target.value }))}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewCampagne(false)}>Annuler</Button>
            <Button onClick={createCampagne} disabled={!newCampagne.nom}>Créer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VenteBureauAgricolePage;
